/**
 * ARIN tab section.
 *
 * Two shapes, one component. With two or more panels it is a tabbed rail. With
 * ONE panel it is a plain product rail with a heading and paging arrows — which
 * is what The ARIN Edit and the best-seller row are. A tab row of one is not a
 * tab row, so it is not built; everything else behaves the same.
 *
 * The tab row is BUILT HERE, not in Liquid. A section cannot read the settings
 * of a Horizon theme block: `section.blocks | where: 'type', ...` returns
 * nothing and `block.settings.label` comes back blank when read from the
 * section, which is why an earlier version rendered a row of unlabelled
 * buttons. Horizon's own sections never read block settings from the section
 * either — layered-slideshow only ever outputs `block.id`. So each panel
 * carries its own label in data-arin-label, which the block CAN read, and this
 * element assembles the row from the panels.
 *
 * WHERE THE ROW IS MOUNTED. Into [data-arin-tabrow] in the section head, so the
 * tabs sit on the same line as the "Shop everything" link and the paging
 * arrows rather than on a line of their own below them. Building the row also
 * sets data-arin-tabbed on this element, which is what the section's CSS keys
 * the head layout off — and what hides the eyebrow, because in a tabbed
 * section the row occupies the eyebrow's place. A single-panel rail never gets
 * here, so it keeps its eyebrow.
 *
 * The mount is a lookup with a fallback rather than an assumption: the head is
 * only rendered when at least one of its own settings is filled in, and if it
 * is absent the row goes back above the panels where it used to live.
 *
 * Progressive enhancement is unchanged in spirit: with no JavaScript there is
 * no row and every panel stays visible and stacked, which shows all the content
 * rather than hiding two thirds of it behind controls that do nothing.
 *
 * The header arrows page the ACTIVE panel's rail. They do not reimplement
 * scrolling — they click Horizon's own carousel controls, which are still in
 * the DOM (visually hidden by blocks/arin-tab) and still own the scroll maths,
 * the snap points and the disabled state at either end. A MutationObserver
 * mirrors that disabled state onto the header buttons, so the arrows grey out
 * exactly when Horizon says the rail has run out.
 */
class ArinTabs extends HTMLElement {
  connectedCallback() {
    this.panelsWrap = this.querySelector('[data-arin-panels]');
    this.panels = Array.from(this.querySelectorAll('.arin-tab[data-arin-label]'));
    this.tabs = [];
    this.index = 0;

    if (!this.panelsWrap || this.panels.length === 0) return;

    // A single panel gets no row and no keyboard handling — there is nothing to
    // switch between — but it still gets its arrows.
    if (this.panels.length > 1) {
      this.buildRow();
      this.addEventListener('keydown', this.onKeydown);
    }

    this.wireArrows();
    this.select(0, { focus: false });
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeydown);
    this.controlObserver?.disconnect();
  }

  buildRow() {
    this.row = document.createElement('div');
    this.row.className = 'arin-tabs__row';
    this.row.setAttribute('role', 'tablist');

    const listLabel = this.dataset.arinTablistLabel;
    if (listLabel) this.row.setAttribute('aria-label', listLabel);

    this.tabs = this.panels.map((panel, i) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'arin-tabs__tab';
      tab.setAttribute('role', 'tab');
      // the panel's id is the source of truth for the pairing; the block writes
      // aria-labelledby against this same derived id
      tab.id = panel.id.replace('arin-panel-', 'arin-tab-');
      tab.setAttribute('aria-controls', panel.id);
      tab.textContent = panel.dataset.arinLabel;
      tab.addEventListener('click', () => this.select(i));
      this.row.appendChild(tab);
      return tab;
    });

    // Into the head beside the link and arrows when the head exists, which is
    // the layout the section is drawn for. data-arin-tabbed is the signal the
    // stylesheet needs, so it is set only on the path that actually lands the
    // row there.
    const mount = this.querySelector('[data-arin-tabrow]');
    if (mount) {
      mount.appendChild(this.row);
      this.setAttribute('data-arin-tabbed', '');
    } else {
      this.panelsWrap.before(this.row);
    }
  }

  wireArrows() {
    this.arrowWrap = this.querySelector('[data-arin-arrows]');
    this.arrows = Array.from(this.querySelectorAll('[data-arin-arrow]'));
    if (!this.arrowWrap || this.arrows.length === 0) return;

    this.arrowWrap.hidden = false;
    this.arrows.forEach((button) => {
      button.addEventListener('click', () => {
        this.rail(Number(button.dataset.arinArrow))?.click();
      });
    });
  }

  /** Horizon's own carousel control for the active panel, or null. */
  rail(direction) {
    const panel = this.panels[this.index];
    if (!panel) return null;
    return panel.querySelector(
      direction < 0 ? '.slideshow-control--previous' : '.slideshow-control--next'
    );
  }

  syncArrows() {
    this.arrows?.forEach((button) => {
      const control = this.rail(Number(button.dataset.arinArrow));
      button.disabled = !control || control.disabled;
    });
  }

  /** Re-point the observer at the active panel's controls. */
  watchControls() {
    this.controlObserver?.disconnect();
    if (!this.arrows?.length) return;

    const panel = this.panels[this.index];
    const controls = panel.querySelectorAll(
      '.slideshow-control--previous, .slideshow-control--next'
    );

    this.controlObserver = new MutationObserver(() => this.syncArrows());
    controls.forEach((control) =>
      this.controlObserver.observe(control, { attributes: true, attributeFilter: ['disabled'] })
    );

    this.syncArrows();
  }

  onKeydown = (event) => {
    const from = this.tabs.indexOf(document.activeElement);
    if (from === -1) return;

    const last = this.tabs.length - 1;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let to = null;

    switch (event.key) {
      case 'ArrowRight':
        to = rtl ? from - 1 : from + 1;
        break;
      case 'ArrowLeft':
        to = rtl ? from + 1 : from - 1;
        break;
      case 'Home':
        to = 0;
        break;
      case 'End':
        to = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    if (to < 0) to = last;
    if (to > last) to = 0;
    this.select(to);
  };

  select(index, { focus = true } = {}) {
    this.index = index;

    // Only a tabbed section hides panels. With one panel there is nothing to
    // hide, and hiding it would blank the section for anyone whose JS ran.
    if (this.tabs.length > 1) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        // roving tabindex: one stop for the whole row, arrows move within it
        tab.tabIndex = on ? 0 : -1;
        this.panels[i].hidden = !on;
      });

      if (focus) this.tabs[index].focus();
    }

    // A rail the customer left halfway through must not be inherited by the
    // tab they switch to — otherwise Essentials opens on its fifth product.
    this.panels[index].querySelectorAll('*').forEach((el) => {
      if (el.scrollLeft > 0 && el.scrollWidth > el.clientWidth) {
        el.scrollTo({ left: 0, behavior: 'instant' });
      }
    });

    // after the panel is visible again, so Horizon has measured it
    requestAnimationFrame(() => this.watchControls());
  }
}

if (!customElements.get('arin-tabs')) customElements.define('arin-tabs', ArinTabs);
